import { lazy, Suspense, type ReactNode } from 'react';
import { Route, Routes } from 'react-router-dom';

import {
  PublicOnly,
  RequireAuth,
  RequireAdmin,
  RequireOnboarding,
  SkipIfOnboarded,
} from '@/app/guards';
import { Spinner } from '@/components/ui';

/**
 * Route table.
 *
 * Every route here resolves to a real, working page — there are no stubs and no
 * dead links anywhere in the UI. Routes are added as their features land.
 *
 * The landing page is imported eagerly because it is the first paint for almost
 * every visitor, and code-splitting it would only add a round-trip. Secondary
 * pages are lazy so they stay out of the initial bundle.
 */
const LandingPage = lazy(() => import('@/features/landing/LandingPage'));
const StatusPage = lazy(() => import('@/features/status/StatusPage'));
const NotFoundPage = lazy(() => import('@/features/misc/NotFoundPage'));
const ForbiddenPage = lazy(() => import('@/features/misc/ForbiddenPage'));
const SignInPage = lazy(() => import('@/features/auth/SignInPage'));
const SignUpPage = lazy(() => import('@/features/auth/SignUpPage'));
const ForgotPasswordPage = lazy(() => import('@/features/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/features/auth/ResetPasswordPage'));
const AuthCallbackPage = lazy(() => import('@/features/auth/AuthCallbackPage'));
const OnboardingPage = lazy(() => import('@/features/onboarding/OnboardingPage'));
const AccountPage = lazy(() => import('@/features/account/AccountPage'));
const InterviewPage = lazy(() => import('@/features/interview/InterviewsPage'));
const InterviewSessionPage = lazy(() => import('@/features/interview/InterviewSessionPage'));
const CodingPage = lazy(() => import('@/features/coding/CodingPage'));
const CodingSessionPage = lazy(() => import('@/features/coding/CodingSessionPage'));
const DashboardPage = lazy(() => import('@/features/dashboard/DashboardPage'));
const MentorPage = lazy(() => import('@/features/mentor/MentorPage'));
const MentorSessionPage = lazy(() => import('@/features/mentor/MentorSessionPage'));
const RoadmapPage = lazy(() => import('@/features/roadmap/RoadmapPage'));
const RoadmapDetailPage = lazy(() => import('@/features/roadmap/RoadmapDetailPage'));
const CompaniesPage = lazy(() => import('@/features/companies/CompaniesPage'));
const CompanyDetailPage = lazy(() => import('@/features/companies/CompanyDetailPage'));
const LeaderboardPage = lazy(() => import('@/features/leaderboard/LeaderboardPage'));
const ResumePage = lazy(() => import('@/features/resume/ResumePage'));
const AdminPage = lazy(() => import('@/features/admin/AdminPage'));
const AdminUserDetailPage = lazy(() => import('@/features/admin/AdminUserDetailPage'));

function RouteFallback() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <Spinner size="lg" label="Loading page" />
    </div>
  );
}

function withSuspense(element: ReactNode): ReactNode {
  return <Suspense fallback={<RouteFallback />}>{element}</Suspense>;
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={withSuspense(<LandingPage />)} />
      <Route
        path="/status"
        element={withSuspense(
          <RequireAdmin>
            <StatusPage />
          </RequireAdmin>,
        )}
      />

      <Route
        path="/sign-in"
        element={withSuspense(
          <PublicOnly>
            <SignInPage />
          </PublicOnly>,
        )}
      />
      <Route
        path="/sign-up"
        element={withSuspense(
          <PublicOnly>
            <SignUpPage />
          </PublicOnly>,
        )}
      />
      <Route
        path="/forgot-password"
        element={withSuspense(
          <PublicOnly>
            <ForgotPasswordPage />
          </PublicOnly>,
        )}
      />
      <Route path="/auth/reset-password" element={withSuspense(<ResetPasswordPage />)} />
      <Route path="/auth/callback" element={withSuspense(<AuthCallbackPage />)} />

      <Route
        path="/onboarding"
        element={withSuspense(
          <RequireAuth>
            <SkipIfOnboarded>
              <OnboardingPage />
            </SkipIfOnboarded>
          </RequireAuth>,
        )}
      />
      <Route
        path="/account"
        element={withSuspense(
          <RequireAuth>
            <RequireOnboarding>
              <AccountPage />
            </RequireOnboarding>
          </RequireAuth>,
        )}
      />
      <Route
        path="/resume"
        element={withSuspense(
          <RequireAuth>
            <RequireOnboarding>
              <ResumePage />
            </RequireOnboarding>
          </RequireAuth>,
        )}
      />
      <Route
        path="/interviews"
        element={withSuspense(
          <RequireAuth>
            <RequireOnboarding>
              <InterviewPage />
            </RequireOnboarding>
          </RequireAuth>,
        )}
      />
      <Route
        path="/interviews/:interviewId"
        element={withSuspense(
          <RequireAuth>
            <RequireOnboarding>
              <InterviewSessionPage />
            </RequireOnboarding>
          </RequireAuth>,
        )}
      />
      <Route
        path="/coding"
        element={withSuspense(
          <RequireAuth>
            <RequireOnboarding>
              <CodingPage />
            </RequireOnboarding>
          </RequireAuth>,
        )}
      />
      <Route
        path="/coding/:sessionId"
        element={withSuspense(
          <RequireAuth>
            <RequireOnboarding>
              <CodingSessionPage />
            </RequireOnboarding>
          </RequireAuth>,
        )}
      />
      <Route
        path="/dashboard"
        element={withSuspense(
          <RequireAuth>
            <RequireOnboarding>
              <DashboardPage />
            </RequireOnboarding>
          </RequireAuth>,
        )}
      />
      <Route
        path="/mentor"
        element={withSuspense(
          <RequireAuth>
            <RequireOnboarding>
              <MentorPage />
            </RequireOnboarding>
          </RequireAuth>,
        )}
      />
      <Route
        path="/mentor/:sessionId"
        element={withSuspense(
          <RequireAuth>
            <RequireOnboarding>
              <MentorSessionPage />
            </RequireOnboarding>
          </RequireAuth>,
        )}
      />
      <Route
        path="/roadmap"
        element={withSuspense(
          <RequireAuth>
            <RequireOnboarding>
              <RoadmapPage />
            </RequireOnboarding>
          </RequireAuth>,
        )}
      />
      <Route
        path="/roadmap/:roadmapId"
        element={withSuspense(
          <RequireAuth>
            <RequireOnboarding>
              <RoadmapDetailPage />
            </RequireOnboarding>
          </RequireAuth>,
        )}
      />
      <Route path="/companies" element={withSuspense(<CompaniesPage />)} />
      <Route path="/companies/:slug" element={withSuspense(<CompanyDetailPage />)} />
      <Route path="/leaderboard" element={withSuspense(<LeaderboardPage />)} />

      <Route
        path="/admin"
        element={withSuspense(
          <RequireAuth>
            <RequireOnboarding>
              <RequireAdmin>
                <AdminPage />
              </RequireAdmin>
            </RequireOnboarding>
          </RequireAuth>,
        )}
      />
      <Route
        path="/admin/users/:userId"
        element={withSuspense(
          <RequireAuth>
            <RequireOnboarding>
              <RequireAdmin>
                <AdminUserDetailPage />
              </RequireAdmin>
            </RequireOnboarding>
          </RequireAuth>,
        )}
      />

      <Route path="/403" element={withSuspense(<ForbiddenPage />)} />
      <Route path="*" element={withSuspense(<NotFoundPage />)} />
    </Routes>
  );
}
